import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader, Navigation } from 'lucide-react';
import api from '../services/api';

interface LocationSearchInputBackendProps {
  value: string;
  onChange: (value: string, placeDetails?: any) => void;
  placeholder?: string;
  label?: string;
  onPlaceSelected?: (place: any) => void;
  onCurrentLocationClick?: () => void;
}

interface Place {
  place_id: string;
  description: string;
  main_text?: string;
  secondary_text?: string;
}

const LocationSearchInputBackend: React.FC<LocationSearchInputBackendProps> = ({
  value,
  onChange,
  placeholder = "Search for a location...",
  label,
  onPlaceSelected,
  onCurrentLocationClick
}) => {
  const [suggestions, setSuggestions] = useState<Place[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSelectingRef = useRef(false);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedPlaceIdRef = useRef<string | null>(null);
  const isInputFocusedRef = useRef(false);

  useEffect(() => {
    // Don't fetch suggestions if we're in the middle of selecting a place
    if (isSelectingRef.current) {
      return;
    }

    // Only fetch suggestions if the input is focused (user is actively typing)
    // Don't fetch when value changes programmatically (e.g., from map pin movement)
    if (!isInputFocusedRef.current) {
      // If input is not focused, just clear suggestions
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // If value is empty, clear suggestions
    if (!value || value.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Debounce API call
    debounceTimer.current = setTimeout(() => {
      // Double-check we're not selecting and input is still focused before fetching
      if (!isSelectingRef.current && isInputFocusedRef.current) {
        fetchSuggestions(value);
      }
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [value]);

  const fetchSuggestions = async (query: string, retryCount = 0): Promise<void> => {
    // Validate input length: must be between 2 and 100 characters
    const trimmedQuery = query?.trim() || '';
    if (trimmedQuery.length < 2) {
      setSuggestions([]);
      return;
    }
    
    // Truncate to 100 characters if longer (backend requirement)
    const sanitizedQuery = trimmedQuery.length > 100 ? trimmedQuery.substring(0, 100) : trimmedQuery;

    setLoading(true);

    try {
      const response = await api.get('/api/places/autocomplete', {
        params: { input: sanitizedQuery }
      });

      const data = response.data;

      // Handle error responses
      if (data.success === false) {
        console.error('API error:', data.message);
        // Retry on server errors (5xx) or network errors
        if ((data.statusCode >= 500 || data.statusCode === 503) && retryCount < 2) {
          console.log(`Retrying autocomplete request (attempt ${retryCount + 1}/2)...`);
          await new Promise(resolve => setTimeout(resolve, 500 * (retryCount + 1)));
          return fetchSuggestions(query, retryCount + 1);
        }
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      // Handle Google Places API response format
      if (data.status === 'OK' && data.predictions && Array.isArray(data.predictions)) {
        // Don't show suggestions if we're in the middle of selecting a place
        if (isSelectingRef.current) {
          setSuggestions([]);
          setShowSuggestions(false);
          return;
        }

        const formattedSuggestions: Place[] = data.predictions.map((pred: any) => ({
          place_id: pred.place_id,
          description: pred.description,
          main_text: pred.structured_formatting?.main_text || pred.description.split(',')[0],
          secondary_text: pred.structured_formatting?.secondary_text || pred.description,
        }));

        setSuggestions(formattedSuggestions);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error: any) {
      console.error('Error fetching suggestions:', error);
      
      // Retry on network errors or 5xx status codes
      const isRetryable = 
        error?.response?.status >= 500 || 
        error?.response?.status === 503 ||
        error?.message?.includes('Network') ||
        error?.code === 'ECONNABORTED' ||
        error?.code === 'ERR_NETWORK';
      
      if (isRetryable && retryCount < 2) {
        console.log(`Retrying autocomplete request after error (attempt ${retryCount + 1}/2)...`);
        await new Promise(resolve => setTimeout(resolve, 500 * (retryCount + 1)));
        return fetchSuggestions(query, retryCount + 1);
      }
      
      // Don't update suggestions if we're in the middle of selecting a place
      if (!isSelectingRef.current) {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlace = async (place: Place, retryCount = 0): Promise<void> => {
    // Prevent multiple calls for the same place
    if (selectedPlaceIdRef.current === place.place_id || isSelectingRef.current) {
      console.log('handleSelectPlace: Already processing this place, ignoring duplicate call');
      return;
    }
    
    // Mark this place as being selected
    selectedPlaceIdRef.current = place.place_id;
    isSelectingRef.current = true;
    
    // Clear any pending blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    
    // Clear any pending debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    
    // Close dropdown immediately and clear suggestions
    setShowSuggestions(false);
    setSuggestions([]);
    
    // Blur the input to prevent focus issues
    inputRef.current?.blur();
    
    // Update the value immediately so user sees feedback
    onChange(place.description);

    // Fetch place details to get coordinates with retry logic
    try {
      const response = await api.get('/api/places/details', {
        params: { place_id: place.place_id }
      });

      const data = response.data;

      if (data.status === 'OK' && data.result?.geometry?.location) {
        const placeDetails = {
          place_id: place.place_id,
          formatted_address: place.description,
          geometry: {
            location: {
              lat: () => data.result.geometry.location.lat,
              lng: () => data.result.geometry.location.lng,
            }
          },
          name: data.result.name || place.description.split(',')[0]
        };

        if (onPlaceSelected) {
          onPlaceSelected(placeDetails);
        }
        onChange(place.description, placeDetails);
      } else {
        // If status is not OK, try retry if it's a server error
        if ((data.statusCode >= 500 || data.statusCode === 503) && retryCount < 2) {
          console.log(`Retrying place details request (attempt ${retryCount + 1}/2)...`);
          await new Promise(resolve => setTimeout(resolve, 500 * (retryCount + 1)));
          return handleSelectPlace(place, retryCount + 1);
        }
        
        if (onPlaceSelected) {
          onPlaceSelected({ place_id: place.place_id, formatted_address: place.description });
        }
      }
    } catch (error: any) {
      console.error('Error fetching place details:', error);
      
      // Retry on network errors or 5xx status codes
      const isRetryable = 
        error?.response?.status >= 500 || 
        error?.response?.status === 503 ||
        error?.message?.includes('Network') ||
        error?.code === 'ECONNABORTED' ||
        error?.code === 'ERR_NETWORK';
      
      if (isRetryable && retryCount < 2) {
        console.log(`Retrying place details request after error (attempt ${retryCount + 1}/2)...`);
        await new Promise(resolve => setTimeout(resolve, 500 * (retryCount + 1)));
        return handleSelectPlace(place, retryCount + 1);
      }
      
      // Even if details fetch fails, still call onPlaceSelected with basic info
      if (onPlaceSelected) {
        onPlaceSelected({ place_id: place.place_id, formatted_address: place.description });
      }
    } finally {
      // Reset after a delay to ensure blur handler doesn't interfere
      setTimeout(() => {
        isSelectingRef.current = false;
        selectedPlaceIdRef.current = null;
      }, 500);
    }
  };

  return (
    <div className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <MapPin className="inline h-4 w-4 mr-1" />
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => {
            // Don't show suggestions if we're in the middle of selecting a place
            if (isSelectingRef.current) {
              onChange(e.target.value);
              return;
            }
            
            onChange(e.target.value);
            if (e.target.value.length >= 2) {
              setShowSuggestions(true);
            } else {
              setShowSuggestions(false);
            }
          }}
          onFocus={() => {
            // Mark input as focused
            isInputFocusedRef.current = true;
            
            // Don't show suggestions if we're selecting
            if (isSelectingRef.current) {
              return;
            }
            if (suggestions.length > 0 && value.length >= 2) {
              setShowSuggestions(true);
            }
          }}
          onBlur={() => {
            // Mark input as not focused
            isInputFocusedRef.current = false;
            
            // Clear any existing timeout
            if (blurTimeoutRef.current) {
              clearTimeout(blurTimeoutRef.current);
            }
            
            // Delay hiding to allow onPress to fire, but check if we're selecting
            blurTimeoutRef.current = setTimeout(() => {
              if (!isSelectingRef.current) {
                setShowSuggestions(false);
              }
              blurTimeoutRef.current = null;
            }, 200);
          }}
          placeholder={placeholder}
          className={`w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-black ${onCurrentLocationClick ? 'pr-20' : loading ? 'pr-10' : ''}`}
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
          {onCurrentLocationClick && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onCurrentLocationClick();
              }}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
              title="Use current location"
            >
              <Navigation className="h-4 w-4 text-green-600" />
            </button>
          )}
          {loading && (
            <Loader className="h-4 w-4 animate-spin text-black600" />
          )}
        </div>
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((place) => (
            <button
              key={place.place_id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Only process if not already selecting
                if (!isSelectingRef.current && selectedPlaceIdRef.current !== place.place_id) {
                  handleSelectPlace(place);
                }
              }}
              className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none flex items-start"
            >
              <MapPin className="h-4 w-4 text-black600 mr-2 mt-1 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {place.main_text || place.description.split(',')[0]}
                </div>
                <div className="text-sm text-gray-500 truncate">
                  {place.secondary_text || place.description}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationSearchInputBackend;

